import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import Swal from "sweetalert2";

import ModuloEditaUsuarios from "./editaUsuario";
import ModuloCreaUsuario from "./creaUsuario";

import ObtieneDatosCookies from "../tools/obtieneDatosCookiesUsuario";

import FooterCorp from "./footerCorp";

// ============================================================
// CATEGORÍAS MOODLE
// ============================================================

const OPCIONES_DEPENDENCIAS = [
  {
    label: "Todos",
    value: 0,
  },
  {
    label: "Escuela de Policía",
    value: 1,
  },
  {
    label: "Escuela de Especialidades",
    value: 292,
  },
  {
    label: "Escuela Superior",
    value: 169,
  },
  {
    label: "Educación a Distancia",
    value: 166,
  },
  {
    label: "Escuela de Investigaciones",
    value: 165,
  },
  {
    label: "Extracurriculares",
    value: 469,
  },
];

// Vista recursiva: respeta la relación padre/hijo y distingue personal directo y total.
function GrupoDependenciaUsuarios({ nodo, nivel = 0, abiertos, alternar, renderUsuario }) {
  const abierto = Boolean(abiertos[nodo.id]);
  return (
    <div>
      <button
        type="button"
        onClick={() => alternar(nodo.id)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, width: "100%", textAlign: "left", cursor: "pointer",
          border: 0, borderBottom: "1px solid #d5dee9", padding: "12px 16px",
          paddingLeft: 16 + nivel * 18, background: nivel ? "#f2f6fa" : "#e8eef5",
          color: "#172b40"
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span>{abierto ? "▼" : "▶"}</span>
          <strong>🏢 {nodo.descripcion}</strong>
        </span>
        <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
          {nodo.directos.length} directos · <b>{nodo.total} total</b>
        </span>
      </button>
      {abierto && (
        <div>
          {nodo.directos.map(renderUsuario)}
          {nodo.hijos.map(hijo => (
            <GrupoDependenciaUsuarios
              key={hijo.id} nodo={hijo} nivel={nivel + 1}
              abiertos={abiertos} alternar={alternar} renderUsuario={renderUsuario}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

function ModuloUsuarios() {

  const navigate = useNavigate();


  const usuarioLogueado = ObtieneDatosCookies();

  // ==========================================================
  // DEPENDENCIAS
  //
  // La jerarquía acepta dependenciaPadreId, dependencia_padre_id,
  // dependeDe y otras variantes compatibles.
  // ==========================================================

  // Misma fuente de dependencias que AdministradorDependencias:
  // POST /dependencias, con respaldo POST /dependencias/arbol.
  const [dependencias, setDependencias] = useState([]);
  const [cargandoDependencias, setCargandoDependencias] = useState(true);
  const [errorDependencias, setErrorDependencias] = useState("");

  useEffect(() => {
    let activo = true;
    const base = (process.env.REACT_APP_BASEURL || "/expedientes/").replace(/\/?$/, "/");
    const token = localStorage.getItem("tokenJWT");
    const config = {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    };

    const aplanarArbol = (nodos, padre = null) =>
      nodos.flatMap((n) => [
        {
          ...n,
          dependenciaPadreId:
            n.dependenciaPadreId ?? n.dependencia_padre_id ?? n.dependeDe ?? padre,
          hijos: undefined,
        },
        ...aplanarArbol(n.hijos || [], n.id),
      ]);

    const cargarDependencias = async () => {
      try {
        let registros;
        try {
          const { data } = await axios.post(`${base}dependencias`, {}, config);
          registros = Array.isArray(data) ? data : data?.data;
          if (!Array.isArray(registros)) throw new Error("Listado de dependencias inválido");
        } catch (errorListado) {
          const { data } = await axios.post(`${base}dependencias/arbol`, {}, config);
          if (!data?.ok || !Array.isArray(data.data)) {
            throw new Error(data?.message || "No se pudo obtener el árbol de dependencias");
          }
          registros = aplanarArbol(data.data);
        }
        if (activo) {
          setDependencias(registros);
          setErrorDependencias("");
        }
      } catch (error) {
        console.error("Error cargando dependencias:", error);
        if (activo) {
          setErrorDependencias(error?.response?.data?.mensaje || error.message);
          setDependencias([]);
        }
      } finally {
        if (activo) setCargandoDependencias(false);
      }
    };

    cargarDependencias();
    return () => { activo = false; };
  }, []);
  const URI =
    "https://wwwa.isepsantafe.net.ar/expedientes/usuarios/";

  // ==========================================================
  // USUARIOS
  // ==========================================================

  const [usuarios, setUsuarios] =
    useState([]);

  const [cargando, setCargando] =
    useState(true);

  const [
    usuarioSeleccionado,
    setUsuarioSeleccionado,
  ] = useState(null);

  // ==========================================================
  // MODALES
  // ==========================================================

  const [modalEditar, setModalEditar] =
    useState(false);

  const [modalCrear, setModalCrear] =
    useState(false);

  const [modalAulas, setModalAulas] =
    useState(false);

  // ==========================================================
  // FILTROS
  // ==========================================================

  const [busqueda, setBusqueda] =
    useState("");

  const [filtroNivel, setFiltroNivel] =
    useState("Todos");

  const [filtroEstado, setFiltroEstado] =
    useState("Todos");

  const [filtroDestino, setFiltroDestino] =
    useState("Todos");

  const [
    ordenAlfabetico,
    setOrdenAlfabetico,
  ] = useState("AZ");

  const [
    agruparPorDestino,
    setAgruparPorDestino,
  ] = useState(false);

  const [
    gruposAbiertos,
    setGruposAbiertos,
  ] = useState({});

  // ==========================================================
  // VIRTUALIZACIÓN
  // ==========================================================

  const [
    procesandoVirtual,
    setProcesandoVirtual,
  ] = useState(null);

  // ==========================================================
  // SESIÓN
  // ==========================================================

  // La ausencia de la cookie antigua "id" no provoca redirección.
  // La autenticación y los permisos se verifican en el backend.

  // ==========================================================
  // CARGAR USUARIOS
  // ==========================================================

  const getUsuarios = async () => {
    try {
      setCargando(true);

      const response =
        await axios.post(URI);

      const datos =
        Array.isArray(response.data)
          ? response.data
          : [];

      setUsuarios(datos);
    } catch (error) {
      console.error(
        "Error obteniendo usuarios:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron obtener los usuarios.",
      });

      setUsuarios([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    getUsuarios();
  }, []);

  // ==========================================================
  // NOMBRE USUARIO
  // ==========================================================

  const obtenerNombreUsuario = (
    usuario
  ) => {
    if (!usuario) {
      return "";
    }

    const apellido =
      usuario.apellido || "";

    const nombre =
      usuario.nombre || "";

    const completo =
      `${apellido} ${nombre}`.trim();

    return (
      completo ||
      usuario.usuario ||
      usuario.identificacion ||
      `Usuario ${usuario.id}`
    );
  };

  // ==========================================================
  // INICIALES
  // ==========================================================

  const obtenerIniciales = (
    usuario
  ) => {
    return obtenerNombreUsuario(usuario)
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((palabra) =>
        palabra
          .charAt(0)
          .toUpperCase()
      )
      .join("");
  };

  // ==========================================================
  // DESTINO
  // ==========================================================

  const obtenerDestino = (
    usuario
  ) => {
    if (!usuario) {
      return "Sin destino";
    }

    const dependenciaUsuario =
      dependencias.find(
        (categoria) =>
          String(categoria.id) ===
          String(usuario.destino)
      );

    return (
      dependenciaUsuario?.descripcion ||
      "Sin destino"
    );
  };

  // ==========================================================
  // IDENTIFICACIÓN
  // ==========================================================

  const obtenerIdentificacion = (
    usuario
  ) => {
    return (
      usuario?.identificacion ||
      usuario?.dni ||
      usuario?.documento ||
      usuario?.usuario ||
      ""
    );
  };

  // ==========================================================
  // PERMISOS GENERALES DE LA PANTALLA
  // ==========================================================

  // Permiso especial: acceso transversal a destinos, con las restricciones de ámbito correspondientes.
  const tienePermisoCambioGlobal = String(usuarioLogueado?.funcion || "")
    .split(",").some(p => p.trim().toLowerCase() === "cambio los destinos de todos");

  const puedeAdministrar =
    Number(usuarioLogueado?.nivel) <= 2 || tienePermisoCambioGlobal;

  // La vista jerárquica está habilitada para administradores (niveles 1 y 2).
  const puedeVerJerarquia =
    [1, 2].includes(Number(usuarioLogueado?.nivel)) || tienePermisoCambioGlobal;

  const puedeCrear =
    Number(usuarioLogueado?.nivel) <= 2 &&
    String(
      usuarioLogueado?.destino
    ) === "7";

  const puedeCambiarContrasena =
    Number(usuarioLogueado?.nivel) < 5;

  // ==========================================================
  // REGLA DE MODIFICACIÓN POR SEDE
  // ==========================================================
  //
  // sedeID 1 = Provincial -> puede modificar cualquier sede.
  // sedeID 2 = Rosario    -> sólo usuarios de Rosario.
  // sedeID 3 = Recreo     -> sólo usuarios de Recreo.
  //
  // Esta validación se aplica a:
  // - Editar usuario
  // - Permisos de Aulas
  // - Virtualizar / Desvirtualizar
  // ==========================================================

  const puedeModificarUsuarioPorSede = (usuario) => {
    const sedeUsuarioLogueado = Number(
      usuarioLogueado?.sedeID ??
      usuarioLogueado?.sedeId ??
      usuarioLogueado?.sede ??
      0
    );

    const sedeUsuarioObjetivo = Number(
      usuario?.sedeID ??
      usuario?.sedeId ??
      usuario?.sede ??
      0
    );

    // Provincial ve y modifica todas las sedes.
    if (sedeUsuarioLogueado === 1) {
      return true;
    }

    // Rosario sólo Rosario / Recreo sólo Recreo.
    if (
      sedeUsuarioLogueado === 2 ||
      sedeUsuarioLogueado === 3
    ) {
      return sedeUsuarioObjetivo === sedeUsuarioLogueado;
    }

    // Si aparece una sede no contemplada, por seguridad
    // sólo permitimos modificar usuarios de la misma sede.
    return (
      sedeUsuarioLogueado > 0 &&
      sedeUsuarioObjetivo === sedeUsuarioLogueado
    );
  };

  const validarModificacionPorSede = (usuario) => {
    if (puedeModificarUsuarioPorSede(usuario)) {
      return true;
    }

    const sedeLogueado = Number(
      usuarioLogueado?.sedeID ??
      usuarioLogueado?.sedeId ??
      usuarioLogueado?.sede ??
      0
    );

    const nombreSede =
      sedeLogueado === 2
        ? "Rosario"
        : sedeLogueado === 3
          ? "Recreo"
          : "su sede";

    Swal.fire({
      icon: "warning",
      title: "Sin autorización para esa sede",
      text:
        sedeLogueado === 1
          ? "No se pudo determinar la sede del usuario seleccionado."
          : `Sólo puede modificar usuarios pertenecientes a ${nombreSede}.`,
    });

    return false;
  };

  // ==========================================================
  // IMPORTANTE
  //
  // Esto NO determina ya qué usuarios puede ver.
  //
  // Solamente determina las funciones especiales:
  //
  // - Crear usuario
  // - Permisos de Aulas
  // - Virtualizar
  // - Desvirtualizar
  // - Filtros generales
  // - Agrupar
  //
  // ==========================================================

  const esAdministradorGeneral =
    String(
      usuarioLogueado?.destino
    ) === "7";

  // ==========================================================
  // OBTENER ID DE LA DEPENDENCIA PADRE
  // ==========================================================
  //
  // En distintos lugares del sistema el campo padre puede venir
  // con nombres diferentes. Centralizamos la lectura acá para que
  // la jerarquía funcione independientemente del formato recibido.
  // ==========================================================

  const obtenerIdDependenciaPadre = (dependencia) => {
    if (!dependencia) {
      return null;
    }

    const padre =
      dependencia.dependenciaPadreId ??
      dependencia.dependencia_padre_id ??
      dependencia.dependenciaPadreID ??
      dependencia.dependencia_padreId ??
      dependencia.padreId ??
      dependencia.padre_id ??
      dependencia.dependeDe ??
      dependencia.dependenciaPadre?.id ??
      dependencia.padre?.id ??
      dependencia.dataValues?.dependenciaPadreId ??
      dependencia.dataValues?.dependencia_padre_id ??
      dependencia.dataValues?.dependeDe ??
      null;

    if (
      padre === null ||
      padre === undefined ||
      padre === ""
    ) {
      return null;
    }

    return String(padre);
  };

  // ==========================================================
  // OBTENER DEPENDENCIA + TODOS SUS DESCENDIENTES
  // ==========================================================
  //
  // Ejemplo:
  //
  // CUERPO
  // ├── 1RA COMPAÑÍA
  // │   ├── 1RA SECCIÓN
  // │   └── 2DA SECCIÓN
  // ├── 2DA COMPAÑÍA
  // └── 3RA COMPAÑÍA
  //
  // Si el usuario pertenece a CUERPO, devuelve CUERPO + todas
  // las dependencias que cuelgan debajo, sin límite de profundidad.
  // ==========================================================

  const obtenerDependenciasDescendientes = (
    dependenciaInicialId
  ) => {
    if (
      dependenciaInicialId === undefined ||
      dependenciaInicialId === null ||
      dependenciaInicialId === ""
    ) {
      return [];
    }

    const raiz = String(
      dependenciaInicialId
    );

    const resultado = new Set();

    const recorrer = (dependenciaId) => {
      const idActual = String(
        dependenciaId
      );

      // Evita ciclos si por error hay una dependencia mal enlazada.
      if (resultado.has(idActual)) {
        return;
      }

      // Incluye la propia dependencia.
      resultado.add(idActual);

      // Busca todos los hijos directos usando cualquiera de los
      // nombres posibles del campo padre.
      const hijos = dependencias.filter(
        (dependencia) => {
          const padreId =
            obtenerIdDependenciaPadre(
              dependencia
            );

          return (
            padreId !== null &&
            String(padreId) === idActual
          );
        }
      );

      // Recorre hijos, nietos, bisnietos, etc.
      hijos.forEach((hijo) => {
        if (
          hijo?.id !== undefined &&
          hijo?.id !== null
        ) {
          recorrer(hijo.id);
        }
      });
    };

    recorrer(raiz);

    const salida = Array.from(
      resultado
    );

    console.log(
      "=========================================="
    );
    console.log(
      "🌳 ÁMBITO JERÁRQUICO DE USUARIOS"
    );
    console.log(
      "Destino raíz:",
      raiz
    );
    console.log(
      "Dependencia raíz:",
      dependencias.find(
        (dependencia) =>
          String(dependencia.id) === raiz
      )?.descripcion || "No encontrada"
    );
    console.log(
      "IDs permitidos:",
      salida
    );
    console.table(
      dependencias
        .filter((dependencia) =>
          salida.includes(
            String(dependencia.id)
          )
        )
        .map((dependencia) => ({
          id: dependencia.id,
          descripcion:
            dependencia.descripcion,
          padre:
            obtenerIdDependenciaPadre(
              dependencia
            ),
        }))
    );
    console.log(
      "=========================================="
    );

    return salida;
  };

  // ==========================================================
  // DESTINOS PERMITIDOS
  // ==========================================================
  //
  // NIVEL 1
  // null = todos
  //
  // NIVEL 2
  // propio + hijos + nietos + ...
  //
  // NIVEL 3/4
  // propio
  //
  // ==========================================================

  const destinosPermitidosAdministrador =
    useMemo(() => {
      const nivel =
        Number(
          usuarioLogueado?.nivel
        );

      const destino =
        usuarioLogueado?.destino;

      // Permiso global: acceso a ramas autorizadas.
      if (tienePermisoCambioGlobal && nivel !== 1) {
        if (!dependencias.length) return [];
        if (String(destino) === "7") return dependencias.map(d => String(d.id));
        const reservados = new Set(obtenerDependenciasDescendientes("7"));
        return dependencias.map(d => String(d.id)).filter(id => !reservados.has(id));
      }

      // SUPER USER
      if (nivel === 1) {
        return null;
      }

      // ADMINISTRADOR
      if (nivel === 2) {
        return obtenerDependenciasDescendientes(
          destino
        );
      }

      // RESTO
      if (
        destino !== undefined &&
        destino !== null &&
        destino !== ""
      ) {
        return [
          String(destino),
        ];
      }

      return [];
    }, [
      usuarioLogueado?.nivel,
      usuarioLogueado?.destino,
      dependencias,
      tienePermisoCambioGlobal,
    ]);

  // ==========================================================
  // BASE DE USUARIOS
  //
  // NUEVA LÓGICA JERÁRQUICA
  // ==========================================================

  const usuariosPermitidos =
    useMemo(() => {
      let lista = [
        ...usuarios,
      ];

      const nivelLogueado =
        Number(
          usuarioLogueado?.nivel
        );

      const destinoLogueado =
        String(
          usuarioLogueado?.destino ??
            ""
        );

      // ======================================================
      // NIVEL 1
      //
      // VE TODO
      // ======================================================

      if (
        nivelLogueado === 1
      ) {
        return lista;
      }

      // Permiso global: ve usuarios de todas las dependencias salvo 7 y su rama.
      if (tienePermisoCambioGlobal) {
        const permitidos = new Set(destinosPermitidosAdministrador || []);
        return lista.filter(u => permitidos.has(String(u.destino)));
      }

      // ======================================================
      // NO NIVEL 1
      //
      // Conservamos regla:
      // no ven usuarios nivel 1
      // ======================================================

      lista =
        lista.filter(
          (usuario) =>
            Number(
              usuario.nivel
            ) > 1
        );

      // ======================================================
      // NIVEL 2
      //
      // VE:
      // - su propio destino
      // - hijos
      // - nietos
      // - bisnietos
      // - etc.
      //
      // Mantenemos por ahora:
      // solamente usuarios activos
      // ======================================================

      if (
        nivelLogueado === 2
      ) {
        const destinosPermitidos =
          destinosPermitidosAdministrador ||
          [];

        return lista.filter(
          (usuario) =>
            destinosPermitidos.includes(
              String(
                usuario.destino
              )
            ) &&
            String(
              usuario.estado
            ) === "1"
        );
      }

      // ======================================================
      // NIVEL 3 / 4
      //
      // MISMO DESTINO + ACTIVOS
      // ======================================================

      return lista.filter(
        (usuario) =>
          String(
            usuario.destino
          ) ===
            destinoLogueado &&
          String(
            usuario.estado
          ) === "1"
      );
    }, [
      usuarios,
      usuarioLogueado?.nivel,
      usuarioLogueado?.destino,
      destinosPermitidosAdministrador,
      tienePermisoCambioGlobal,
    ]);

  // ==========================================================
  // DESTINOS DISPONIBLES
  // ==========================================================

  const destinosDisponibles =
    useMemo(() => {
      const resultado =
        usuariosPermitidos
          .map((usuario) =>
            obtenerDestino(usuario)
          )
          .filter(
            (destino) =>
              destino &&
              destino !==
                "Sin destino"
          );

      return [
        ...new Set(resultado),
      ].sort((a, b) =>
        String(a).localeCompare(
          String(b),
          "es",
          {
            sensitivity: "base",
          }
        )
      );
    }, [
      usuariosPermitidos,
      dependencias,
    ]);

  // ==========================================================
  // FILTRADO
  // ==========================================================

  const usuariosVisibles =
    useMemo(() => {
      let lista = [
        ...usuariosPermitidos,
      ];

      // ------------------------------------------------------
      // BUSCADOR
      // ------------------------------------------------------

      if (
        busqueda.trim() !== ""
      ) {
        const texto =
          busqueda
            .toLowerCase()
            .trim();

        lista =
          lista.filter(
            (usuario) => {
              const valores = [
                usuario.apellido,
                usuario.nombre,
                usuario.identificacion,
                usuario.usuario,
                obtenerDestino(
                  usuario
                ),
              ];

              return valores.some(
                (valor) =>
                  String(
                    valor || ""
                  )
                    .toLowerCase()
                    .includes(
                      texto
                    )
              );
            }
          );
      }

      // ------------------------------------------------------
      // NIVEL
      // ------------------------------------------------------

      if (
        filtroNivel !==
        "Todos"
      ) {
        lista =
          lista.filter(
            (usuario) =>
              String(
                usuario.nivel
              ) ===
              String(
                filtroNivel
              )
          );
      }

      // ------------------------------------------------------
      // ESTADO
      // ------------------------------------------------------

      if (
        filtroEstado !==
        "Todos"
      ) {
        lista =
          lista.filter(
            (usuario) => {
              const estado =
                String(
                  usuario.estado
                ) === "1"
                  ? "Activo"
                  : "Inactivo";

              return (
                estado ===
                filtroEstado
              );
            }
          );
      }

      // ------------------------------------------------------
      // DESTINO
      // ------------------------------------------------------

      if (
        filtroDestino !==
        "Todos"
      ) {
        lista =
          lista.filter(
            (usuario) =>
              obtenerDestino(
                usuario
              ) ===
              filtroDestino
          );
      }

      // ------------------------------------------------------
      // A-Z / Z-A
      // ------------------------------------------------------

      lista.sort(
        (a, b) => {
          const textoA =
            `${a.apellido || ""} ${
              a.nombre || ""
            }`.trim();

          const textoB =
            `${b.apellido || ""} ${
              b.nombre || ""
            }`.trim();

          const comparacion =
            textoA.localeCompare(
              textoB,
              "es",
              {
                sensitivity:
                  "base",

                numeric: true,
              }
            );

          return ordenAlfabetico ===
            "AZ"
            ? comparacion
            : -comparacion;
        }
      );

      return lista;
    }, [
      usuariosPermitidos,
      busqueda,
      filtroNivel,
      filtroEstado,
      filtroDestino,
      ordenAlfabetico,
      dependencias,
    ]);

  // ==========================================================
  // AGRUPADO
  // ==========================================================

  const usuariosAgrupados =
    useMemo(() => {
      const grupos = {};

      usuariosVisibles.forEach(
        (usuario) => {
          const destino =
            obtenerDestino(
              usuario
            );

          if (
            !grupos[destino]
          ) {
            grupos[destino] =
              [];
          }

          grupos[
            destino
          ].push(usuario);
        }
      );

      return Object.entries(
        grupos
      ).sort(([a], [b]) =>
        a.localeCompare(
          b,
          "es",
          {
            sensitivity:
              "base",
          }
        )
      );
    }, [
      usuariosVisibles,
      dependencias,
    ]);

  // Árbol de destinos con usuarios visibles, incluyendo los padres
  // aunque no tengan usuarios directos. Se cuentan personas, no sedes.
  const jerarquiaUsuarios = useMemo(() => {
    const nodos = new Map();
    dependencias.forEach(d => {
      const id = String(d.id);
      nodos.set(id, {
        id, descripcion: d.descripcion || `Destino ${id}`,
        padre: d.dependenciaPadreId ?? d.dependencia_padre_id ?? d.dependeDe ?? null,
        directos: [], hijos: [], total: 0
      });
    });

    usuariosVisibles.forEach(usuario => {
      const id = String(usuario.destino ?? "sin_destino");
      if (!nodos.has(id)) {
        nodos.set(id, {
          id, descripcion: id === "sin_destino" ? "Sin destino" : `Destino ${id} (sin registrar)`,
          padre: null, directos: [], hijos: [], total: 0
        });
      }
      nodos.get(id).directos.push(usuario);
    });

    const raices = [];
    nodos.forEach(nodo => {
      const padre = nodo.padre == null || nodo.padre === "" ? null : String(nodo.padre);
      if (padre && padre !== nodo.id && nodos.has(padre)) {
        nodos.get(padre).hijos.push(nodo);
      } else {
        raices.push(nodo);
      }
    });

    const visitados = new Set();
    const procesar = (nodo, camino = new Set()) => {
      if (camino.has(nodo.id) || visitados.has(nodo.id)) return null;
      camino.add(nodo.id);
      visitados.add(nodo.id);
      nodo.hijos = nodo.hijos.map(h => procesar(h, camino)).filter(Boolean);
      camino.delete(nodo.id);
      nodo.total = nodo.directos.length + nodo.hijos.reduce((n, h) => n + h.total, 0);
      nodo.hijos.sort((a, b) => a.descripcion.localeCompare(b.descripcion, "es"));
      return nodo.total > 0 ? nodo : null;
    };
    const resultado = raices.map(n => procesar(n)).filter(Boolean);
    // Respaldo ante datos antiguos con referencias circulares.
    nodos.forEach(n => {
      if (!visitados.has(n.id)) {
        const extra = procesar(n);
        if (extra) resultado.push(extra);
      }
    });
    return resultado.sort((a, b) => a.descripcion.localeCompare(b.descripcion, "es"));
  }, [dependencias, usuariosVisibles]);

  const [jerarquiaAbierta, setJerarquiaAbierta] = useState({});
  const alternarJerarquia = (id) => setJerarquiaAbierta(prev => ({
    ...prev, [id]: !prev[id]
  }));

  // ==========================================================
  // ABRIR/CERRAR GRUPO
  // ==========================================================

  const toggleGrupo = (
    destino
  ) => {
    setGruposAbiertos(
      (actual) => ({
        ...actual,

        [destino]:
          actual[destino] ===
          undefined
            ? false
            : !actual[
                destino
              ],
      })
    );
  };

  const grupoEstaAbierto = (
    destino
  ) => {
    return (
      gruposAbiertos[
        destino
      ] !== false
    );
  };

  // ==========================================================
  // CLIPBOARD
  // ==========================================================

  const copiarAlPortapapeles =
    async (texto) => {
      const valor =
        String(
          texto || ""
        ).trim();

      if (!valor) {
        return false;
      }

      try {
        if (
          window.isSecureContext &&
          navigator.clipboard &&
          typeof navigator
            .clipboard
            .writeText ===
            "function"
        ) {
          await navigator.clipboard.writeText(
            valor
          );

          return true;
        }
      } catch (error) {
        console.warn(
          "No se pudo copiar con navigator.clipboard:",
          error
        );
      }

      try {
        const textarea =
          document.createElement(
            "textarea"
          );

        textarea.value =
          valor;

        textarea.setAttribute(
          "readonly",
          ""
        );

        textarea.style.position =
          "fixed";

        textarea.style.top =
          "0";

        textarea.style.left =
          "0";

        textarea.style.width =
          "1px";

        textarea.style.height =
          "1px";

        textarea.style.opacity =
          "0";

        document.body.appendChild(
          textarea
        );

        textarea.focus();

        textarea.select();

        textarea.setSelectionRange(
          0,
          valor.length
        );

        const resultado =
          document.execCommand(
            "copy"
          );

        document.body.removeChild(
          textarea
        );

        return resultado;
      } catch (error) {
        console.error(
          "No se pudo copiar:",
          error
        );

        return false;
      }
    };

  // ==========================================================
  // VIRTUALIZAR / DESVIRTUALIZAR
  // ==========================================================

// ==========================================================
// VIRTUALIZAR / DESVIRTUALIZAR
// ==========================================================

const manejarVirtualizacion = async (usuario) => {
  if (!validarModificacionPorSede(usuario)) {
    return;
  }

  const identificacion = String(
    usuario?.identificacion || ""
  ).trim();

  const estaActivo =
    String(
      usuario?.virtualizado || ""
    ).toLowerCase() === "activo";

  // ========================================================
  // VALIDACIÓN
  // ========================================================

  if (!identificacion) {
    return Swal.fire({
      icon: "error",
      title: "Identificación inválida",
      text:
        "El usuario no posee una identificación válida.",
    });
  }

  // ========================================================
  // NUEVO ESTADO
  // ========================================================

  const nuevoEstado = estaActivo
    ? "Inactivo"
    : "Activo";

  // ========================================================
  // URL
  // ========================================================

  const url = estaActivo
    ? "https://wwwp.isepsantafe.net.ar/miIsep/desvirtualizar-usuario-sigedi"
    : "https://wwwp.isepsantafe.net.ar/miIsep/crear-virtual-usuario-sigedi";

  const accionTexto = estaActivo
    ? "desvirtualizar"
    : "virtualizar";

  // ========================================================
  // CONFIRMACIÓN
  // ========================================================

  const confirmacion = await Swal.fire({
    title: `¿Desea ${accionTexto} este usuario?`,

    html: `
      <div style="text-align:center">
        <strong>
          ${usuario.apellido || ""}, ${usuario.nombre || ""}
        </strong>

        <br/><br/>

        DNI:
        <strong>
          ${identificacion}
        </strong>
      </div>
    `,

    icon: "warning",

    showCancelButton: true,

    confirmButtonText:
      `Sí, ${accionTexto}`,

    cancelButtonText:
      "Cancelar",

    reverseButtons: true,
  });

  if (!confirmacion.isConfirmed) {
    return;
  }

  // ========================================================
  // COPIAR DNI AL VIRTUALIZAR
  // ========================================================

  let identificacionCopiada = false;

  if (!estaActivo) {
    identificacionCopiada =
      await copiarAlPortapapeles(
        identificacion
      );
  }

  try {
    // ======================================================
    // BLOQUEAMOS EL BOTÓN
    // ======================================================

    setProcesandoVirtual(
      identificacion
    );

    console.log(
      "========================================"
    );

    console.log(
      "🔄 CAMBIO DE VIRTUALIZACIÓN"
    );

    console.log({
      identificacion,
      estadoAnterior:
        usuario.virtualizado,
      nuevoEstado,
      url,
    });

    console.log(
      "========================================"
    );

    // ======================================================
    // PETICIÓN AL BACKEND
    // ======================================================

    const response =
      await axios.post(
        url,
        {
          dni:
            identificacion,

          clave:
            "ISEP2026",
        }
      );

    console.log(
      "✅ RESPUESTA VIRTUALIZACIÓN:",
      response.data
    );

    // ======================================================
    // 1. ACTUALIZAR LISTADO DE USUARIOS
    // ======================================================

    setUsuarios(
      (actuales) =>
        actuales.map(
          (u) =>
            String(
              u.identificacion
            ) ===
            String(
              identificacion
            )
              ? {
                  ...u,

                  virtualizado:
                    nuevoEstado,
                }
              : u
        )
    );

    // ======================================================
    // 2. ACTUALIZAR USUARIO SELECCIONADO
    //
    // ESTA ES LA PARTE QUE FALTABA.
    //
    // La ficha de la derecha usa usuarioSeleccionado,
    // no el objeto actualizado del array usuarios.
    // ======================================================

    setUsuarioSeleccionado(
      (actual) => {
        if (!actual) {
          return actual;
        }

        if (
          String(
            actual.identificacion
          ) !==
          String(
            identificacion
          )
        ) {
          return actual;
        }

        const actualizado = {
          ...actual,

          virtualizado:
            nuevoEstado,
        };

        console.log(
          "✅ USUARIO SELECCIONADO ACTUALIZADO:",
          actualizado
        );

        return actualizado;
      }
    );

    // ======================================================
    // DESVIRTUALIZADO
    // ======================================================

    if (estaActivo) {
      await Swal.fire({
        icon: "success",

        title:
          "Usuario desvirtualizado",

        text:
          "El usuario fue desvirtualizado correctamente.",

        timer: 1500,

        showConfirmButton:
          false,
      });

      return;
    }

    // ======================================================
    // VIRTUALIZADO
    // ======================================================

    const resultado =
      await Swal.fire({
        icon:
          "success",

        title:
          "Usuario virtualizado",

        html: `
          <p>
            El usuario fue virtualizado correctamente.
          </p>

          <p>
            Identificación:
            <strong>
              ${identificacion}
            </strong>
          </p>

          <p>
            ${
              identificacionCopiada
                ? "✅ La identificación fue copiada al portapapeles."
                : "⚠️ El navegador no permitió copiar automáticamente."
            }
          </p>
        `,

        confirmButtonText:
          "Aceptar",

        showDenyButton:
          !identificacionCopiada,

        denyButtonText:
          "Copiar identificación",

        allowOutsideClick:
          false,
      });

    // ======================================================
    // COPIAR MANUALMENTE
    // ======================================================

    if (resultado.isDenied) {
      const copiado =
        await copiarAlPortapapeles(
          identificacion
        );

      if (copiado) {
        await Swal.fire({
          icon:
            "success",

          title:
            "Identificación copiada",

          text:
            `Se copió ${identificacion} al portapapeles.`,

          timer:
            1500,

          showConfirmButton:
            false,
        });
      }
    }

  } catch (error) {
    console.error(
      "❌ Error virtualizando usuario:",
      error
    );

    console.error(
      "Respuesta backend:",
      error.response?.data
    );

    await Swal.fire({
      icon:
        "error",

      title:
        "Error",

      text:
        error?.response?.data
          ?.message ||
        error?.response?.data
          ?.error ||
        error?.message ||
        "No se pudo completar la operación.",
    });

  } finally {
    setProcesandoVirtual(
      null
    );
  }
};

  // ==========================================================
  // ACCIONES USUARIO
  // ==========================================================

  const editarUsuario = (
    usuario
  ) => {
    if (!validarModificacionPorSede(usuario)) {
      return;
    }

    setUsuarioSeleccionado(
      usuario
    );

    setModalEditar(true);
  };

  const abrirAulas = (
    usuario
  ) => {
    if (!validarModificacionPorSede(usuario)) {
      return;
    }

    setUsuarioSeleccionado(
      usuario
    );

    setModalAulas(true);
  };

  const limpiarFiltros =
    () => {
      setBusqueda("");

      setFiltroNivel(
        "Todos"
      );

      setFiltroEstado(
        "Todos"
      );

      setFiltroDestino(
        "Todos"
      );

      setOrdenAlfabetico(
        "AZ"
      );
    };

  const volver = () => {
    navigate(
      "/usuarioLogueado"
    );
  };

  const cambiarContrasena =
    () => {
      navigate(
        "/cambioContrasena"
      );
    };

  // ==========================================================
  // ESTILOS
  // ==========================================================

  const estiloControl = {
    width: "100%",

    height: "44px",

    padding:
      "8px 12px",

    border:
      "1px solid #cbd5e1",

    borderRadius:
      "9px",

    background:
      "#fff",

    color:
      "#1e293b",

    fontSize:
      "14px",

    boxSizing:
      "border-box",
  };

  const modalStyles = {
    overlay: {
      backgroundColor:
        "rgba(15,23,42,.70)",

      zIndex:
        9999,

      backdropFilter:
        "blur(3px)",
    },

    content: {
      top: "50%",

      left: "50%",

      right: "auto",

      bottom: "auto",

      transform:
        "translate(-50%, -50%)",

      width: "96%",

      maxWidth:
        "1280px",

      maxHeight:
        "95vh",

      overflowY:
        "auto",

      padding: 0,

      border: "none",

      borderRadius:
        "18px",

      background:
        "#fff",

      boxShadow:
        "0 25px 60px rgba(0,0,0,.30)",
    },
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div
      style={{
        minHeight:
          "100vh",

        background:
          "#f1f5f9",
      }}
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        style={{
          background:
            "linear-gradient(135deg,#172554,#1e3a5f,#275271)",

          color:
            "#fff",

          padding:
            "28px 25px",

          boxShadow:
            "0 5px 18px rgba(0,0,0,.15)",
        }}
      >
        <div
          style={{
            maxWidth:
              "1500px",

            margin:
              "auto",

            display:
              "flex",

            justifyContent:
              "space-between",

            alignItems:
              "center",

            gap:
              "20px",

            flexWrap:
              "wrap",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,

                fontWeight:
                  700,
              }}
            >
              Gestión de Usuarios
            </h2>

            <div
              style={{
                opacity:
                  0.8,

                marginTop:
                  "5px",
              }}
            >
              Usuarios, destinos,
              aulas y accesos
            </div>
          </div>

          <div
            style={{
              display:
                "flex",

              gap:
                "10px",

              flexWrap:
                "wrap",
            }}
          >
            {puedeCambiarContrasena && (
              <button
                type="button"

                className="btn btn-light"

                onClick={
                  cambiarContrasena
                }
              >
                🔐 Mi contraseña
              </button>
            )}

            {puedeCrear && (
              <button
                type="button"

                className="btn btn-success"

                onClick={() =>
                  setModalCrear(
                    true
                  )
                }
              >
                ＋ Crear usuario
              </button>
            )}

            <button
              type="button"

              className="btn btn-warning"

              onClick={
                volver
              }
            >
              ← Volver
            </button>
          </div>
        </div>
      </div>

      {/* ======================================================
          CONTENIDO
      ====================================================== */}

      <div
        style={{
          maxWidth:
            "1500px",

          margin:
            "auto",

          padding:
            "25px",
        }}
      >
        {cargandoDependencias && (
          <p style={{ color: "#64748b" }}>Cargando jerarquía de dependencias...</p>
        )}
        {errorDependencias && (
          <div role="alert" className="alert alert-warning">
            No se pudo cargar la jerarquía de dependencias: {errorDependencias}
          </div>
        )}

        {/* ====================================================
            RESUMEN
        ==================================================== */}

        <div
          style={{
            display:
              "grid",

            gridTemplateColumns:
              "repeat(auto-fit,minmax(180px,1fr))",

            gap:
              "15px",

            marginBottom:
              "20px",
          }}
        >
          <TarjetaResumen
            icono="👥"

            titulo="Usuarios"

            valor={
              usuariosPermitidos.length
            }
          />

          <TarjetaResumen
            icono="🔎"

            titulo="Mostrados"

            valor={
              usuariosVisibles.length
            }
          />

          <TarjetaResumen
            icono="🏢"

            titulo="Destinos"

            valor={
              destinosDisponibles.length
            }
          />

          <TarjetaResumen
            icono="🟢"

            titulo="Activos"

            valor={
              usuariosPermitidos.filter(
                (x) =>
                  String(
                    x.estado
                  ) === "1"
              ).length
            }
          />
        </div>

        {/* ====================================================
            FILTROS
        ==================================================== */}

        <div
          style={{
            background:
              "#fff",

            padding:
              "18px",

            borderRadius:
              "16px",

            marginBottom:
              "20px",

            boxShadow:
              "0 3px 14px rgba(15,23,42,.08)",
          }}
        >
          <div
            style={{
              display:
                "flex",

              flexWrap:
                "wrap",

              gap:
                "10px",

              alignItems:
                "center",
            }}
          >
            <div
              style={{
                flex:
                  "1 1 320px",

                minWidth:
                  "260px",
              }}
            >
              <input
                type="text"

                placeholder="🔍 Buscar apellido, nombre, DNI o destino..."

                value={
                  busqueda
                }

                onChange={(e) =>
                  setBusqueda(
                    e.target.value
                  )
                }

                style={
                  estiloControl
                }
              />
            </div>

            {esAdministradorGeneral && (
              <>
                <div
                  style={{
                    flex:
                      "0 1 180px",

                    minWidth:
                      "165px",
                  }}
                >
                  <select
                    value={
                      filtroNivel
                    }

                    onChange={(e) =>
                      setFiltroNivel(
                        e.target.value
                      )
                    }

                    style={
                      estiloControl
                    }
                  >
                    <option value="Todos">
                      Todos los niveles
                    </option>

                    {[1, 2, 3, 4, 5].map(
                      (nivel) => (
                        <option
                          key={
                            nivel
                          }

                          value={
                            nivel
                          }
                        >
                          Nivel{" "}
                          {nivel}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div
                  style={{
                    flex:
                      "0 1 180px",

                    minWidth:
                      "165px",
                  }}
                >
                  <select
                    value={
                      filtroEstado
                    }

                    onChange={(e) =>
                      setFiltroEstado(
                        e.target.value
                      )
                    }

                    style={
                      estiloControl
                    }
                  >
                    <option value="Todos">
                      Todos los estados
                    </option>

                    <option value="Activo">
                      Activos
                    </option>

                    <option value="Inactivo">
                      Inactivos
                    </option>
                  </select>
                </div>

                <div
                  style={{
                    flex:
                      "1 1 260px",

                    minWidth:
                      "230px",
                  }}
                >
                  <select
                    value={
                      filtroDestino
                    }

                    onChange={(e) =>
                      setFiltroDestino(
                        e.target.value
                      )
                    }

                    style={
                      estiloControl
                    }
                  >
                    <option value="Todos">
                      Todos los destinos
                    </option>

                    {destinosDisponibles.map(
                      (destino) => (
                        <option
                          key={
                            destino
                          }

                          value={
                            destino
                          }
                        >
                          {destino}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </>
            )}

            <div
              style={{
                flex:
                  "0 1 180px",

                minWidth:
                  "165px",
              }}
            >
              <select
                value={
                  ordenAlfabetico
                }

                onChange={(e) =>
                  setOrdenAlfabetico(
                    e.target.value
                  )
                }

                style={
                  estiloControl
                }
              >
                <option value="AZ">
                  Apellido A → Z
                </option>

                <option value="ZA">
                  Apellido Z → A
                </option>
              </select>
            </div>

            <button
              type="button"

              className="btn btn-outline-secondary"

              style={{
                height:
                  "44px",
              }}

              onClick={
                limpiarFiltros
              }
            >
              Limpiar
            </button>
          </div>

          <div
            style={{
              marginTop:
                "15px",

              paddingTop:
                "14px",

              borderTop:
                "1px solid #e2e8f0",

              display:
                "flex",

              justifyContent:
                "space-between",

              alignItems:
                "center",

              gap:
                "10px",

              flexWrap:
                "wrap",
            }}
          >
            <div
              style={{
                fontSize:
                  "14px",

                color:
                  "#64748b",
              }}
            >
              Mostrando{" "}
              <strong>
                {
                  usuariosVisibles.length
                }
              </strong>{" "}
              usuarios
            </div>

            {puedeVerJerarquia && (
              <label
                style={{
                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap:
                    "8px",

                  cursor:
                    "pointer",

                  margin:
                    0,

                  fontWeight:
                    600,
                }}
              >
                <input
                  type="checkbox"

                  checked={
                    agruparPorDestino
                  }

                  onChange={(e) =>
                    setAgruparPorDestino(
                      e.target.checked
                    )
                  }
                />

                Agrupar por jerarquía
              </label>
            )}
          </div>
        </div>

        {/* ====================================================
            LISTADO
        ==================================================== */}

        {puedeAdministrar && (
          <div
            style={{
              display:
                "grid",

              gridTemplateColumns:
                usuarioSeleccionado
                  ? "minmax(520px,2fr) minmax(340px,1fr)"
                  : "1fr",

              gap:
                "20px",
            }}
          >
            <div
              style={{
                background:
                  "#fff",

                borderRadius:
                  "16px",

                overflow:
                  "hidden",

                boxShadow:
                  "0 3px 14px rgba(15,23,42,.08)",
              }}
            >
              <div
                style={{
                  padding:
                    "16px 20px",

                  borderBottom:
                    "1px solid #e2e8f0",

                  display:
                    "flex",

                  justifyContent:
                    "space-between",

                  alignItems:
                    "center",
                }}
              >
                <strong>
                  Usuarios
                </strong>

                <span
                  style={{
                    fontSize:
                      "13px",

                    color:
                      "#64748b",
                  }}
                >
                  {
                    usuariosVisibles.length
                  }{" "}
                  registros
                </span>
              </div>

              {cargando ? (
                <div
                  style={{
                    padding:
                      "60px",

                    textAlign:
                      "center",
                  }}
                >
                  <div
                    className="spinner-border text-primary"
                    role="status"
                  />

                  <div
                    style={{
                      marginTop:
                        "10px",
                    }}
                  >
                    Cargando usuarios...
                  </div>
                </div>
              ) : usuariosVisibles.length ===
                0 ? (
                <div
                  style={{
                    padding:
                      "50px",

                    textAlign:
                      "center",

                    color:
                      "#64748b",
                  }}
                >
                  No se encontraron usuarios.
                </div>
              ) : (
                <div
                  style={{
                    maxHeight:
                      "700px",

                    overflowY:
                      "auto",
                  }}
                >
                  {puedeVerJerarquia && agruparPorDestino
                    ? jerarquiaUsuarios.map(nodo => (
                        <GrupoDependenciaUsuarios
                          key={nodo.id}
                          nodo={nodo}
                          abiertos={jerarquiaAbierta}
                          alternar={alternarJerarquia}
                          renderUsuario={usuario => (
                            <UsuarioFila
                              key={usuario.id || usuario.identificacion}
                              usuario={usuario}
                              destino={obtenerDestino(usuario)}
                              seleccionado={String(usuarioSeleccionado?.identificacion) === String(usuario.identificacion)}
                              onClick={() => setUsuarioSeleccionado(usuario)}
                            />
                          )}
                        />
                      ))
                    : usuariosVisibles.map(
                        (usuario) => (
                          <UsuarioFila
                            key={
                              usuario.id ||
                              usuario.identificacion
                            }

                            usuario={
                              usuario
                            }

                            destino={obtenerDestino(
                              usuario
                            )}

                            seleccionado={
                              String(
                                usuarioSeleccionado?.identificacion
                              ) ===
                              String(
                                usuario.identificacion
                              )
                            }

                            onClick={() =>
                              setUsuarioSeleccionado(
                                usuario
                              )
                            }
                          />
                        )
                      )}
                </div>
              )}
            </div>

            {/* =================================================
                FICHA USUARIO
            ================================================= */}

            {usuarioSeleccionado && (
              <div
                style={{
                  background:
                    "#fff",

                  borderRadius:
                    "16px",

                  alignSelf:
                    "start",

                  overflow:
                    "hidden",

                  boxShadow:
                    "0 3px 14px rgba(15,23,42,.08)",
                }}
              >
                <div
                  style={{
                    background:
                      "linear-gradient(135deg,#1e3a5f,#32739a)",

                    color:
                      "#fff",

                    padding:
                      "28px 20px",

                    textAlign:
                      "center",
                  }}
                >
                  <div
                    style={{
                      width:
                        "82px",

                      height:
                        "82px",

                      margin:
                        "0 auto 12px",

                      borderRadius:
                        "50%",

                      background:
                        "rgba(255,255,255,.18)",

                      display:
                        "flex",

                      alignItems:
                        "center",

                      justifyContent:
                        "center",

                      fontSize:
                        "27px",

                      fontWeight:
                        700,
                    }}
                  >
                    {obtenerIniciales(
                      usuarioSeleccionado
                    )}
                  </div>

                  <h5
                    style={{
                      margin:
                        0,
                    }}
                  >
                    {obtenerNombreUsuario(
                      usuarioSeleccionado
                    )}
                  </h5>

                  <div
                    style={{
                      opacity:
                        0.82,

                      marginTop:
                        "5px",
                    }}
                  >
                    Nivel{" "}
                    {
                      usuarioSeleccionado.nivel
                    }
                  </div>
                </div>

                <div
                  style={{
                    padding:
                      "20px",
                  }}
                >
                  <DatoUsuario
                    titulo="DNI / Identificación"

                    valor={obtenerIdentificacion(
                      usuarioSeleccionado
                    )}
                  />

                  <DatoUsuario
                    titulo="Destino"

                    valor={obtenerDestino(
                      usuarioSeleccionado
                    )}
                  />

                  <DatoUsuario
                    titulo="Sede"

                    valor={
                      Number(
                        usuarioSeleccionado?.sedeID ??
                        usuarioSeleccionado?.sedeId ??
                        usuarioSeleccionado?.sede ??
                        0
                      ) === 1
                        ? "Provincial"
                        : Number(
                              usuarioSeleccionado?.sedeID ??
                              usuarioSeleccionado?.sedeId ??
                              usuarioSeleccionado?.sede ??
                              0
                            ) === 2
                          ? "Rosario"
                          : Number(
                                usuarioSeleccionado?.sedeID ??
                                usuarioSeleccionado?.sedeId ??
                                usuarioSeleccionado?.sede ??
                                0
                              ) === 3
                            ? "Recreo"
                            : "Sin sede"
                    }
                  />

                  <DatoUsuario
                    titulo="Estado"

                    valor={
                      String(
                        usuarioSeleccionado.estado
                      ) ===
                      "1"
                        ? "Activo"
                        : "Inactivo"
                    }
                  />

                  <DatoUsuario
                    titulo="Virtualización"

                    valor={
                      String(
                        usuarioSeleccionado.virtualizado ||
                          ""
                      ).toLowerCase() ===
                      "activo"
                        ? "Activo"
                        : "Inactivo"
                    }
                  />

                  <hr />

                  <button
                    type="button"

                    className="btn btn-primary w-100 mb-2"

                    disabled={
                      !puedeModificarUsuarioPorSede(
                        usuarioSeleccionado
                      )
                    }

                    title={
                      puedeModificarUsuarioPorSede(
                        usuarioSeleccionado
                      )
                        ? "Editar usuario"
                        : "No puede modificar usuarios de otra sede"
                    }

                    onClick={() =>
                      editarUsuario(
                        usuarioSeleccionado
                      )
                    }
                  >
                    ✏️ Editar usuario
                  </button>

                  {/* ==========================================
                      PERMISOS DE AULAS

                      SE MANTIENE EXACTAMENTE PARA DESTINO 7
                  ========================================== */}

                  {esAdministradorGeneral && (
                    <button
                      type="button"

                      className="btn btn-dark w-100 mb-2"

                      disabled={
                        !puedeModificarUsuarioPorSede(
                          usuarioSeleccionado
                        )
                      }

                      onClick={() =>
                        abrirAulas(
                          usuarioSeleccionado
                        )
                      }
                    >
                      🎓 Permisos de Aulas
                    </button>
                  )}

                  {/* ==========================================
                      VIRTUALIZACIÓN

                      SE MANTIENE EXACTAMENTE PARA DESTINO 7
                  ========================================== */}

                  {esAdministradorGeneral && (
                    <button
                      type="button"

                      className={
                        String(
                          usuarioSeleccionado.virtualizado ||
                            ""
                        ).toLowerCase() ===
                        "activo"
                          ? "btn btn-outline-danger w-100 mb-2"
                          : "btn btn-outline-success w-100 mb-2"
                      }

                      disabled={
                        String(
                          procesandoVirtual
                        ) ===
                          String(
                            usuarioSeleccionado.identificacion
                          ) ||
                        !puedeModificarUsuarioPorSede(
                          usuarioSeleccionado
                        )
                      }

                      onClick={() =>
                        manejarVirtualizacion(
                          usuarioSeleccionado
                        )
                      }
                    >
                      {String(
                        procesandoVirtual
                      ) ===
                      String(
                        usuarioSeleccionado.identificacion
                      )
                        ? "Procesando..."
                        : String(
                              usuarioSeleccionado.virtualizado ||
                                ""
                            ).toLowerCase() ===
                            "activo"
                          ? "🔴 Desvirtualizar"
                          : "🟢 Virtualizar"}
                    </button>
                  )}

                  <button
                    type="button"

                    className="btn btn-outline-secondary w-100"

                    onClick={() =>
                      setUsuarioSeleccionado(
                        null
                      )
                    }
                  >
                    Cerrar ficha
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ======================================================
          MODAL EDITAR
      ====================================================== */}

      <Modal
        ariaHideApp={false}

        isOpen={
          modalEditar
        }

        onRequestClose={() =>
          setModalEditar(
            false
          )
        }

        style={
          modalStyles
        }
      >
        <ModalHeader
          titulo="Editar usuario"

          subtitulo={obtenerNombreUsuario(
            usuarioSeleccionado
          )}

          cerrar={() =>
            setModalEditar(
              false
            )
          }
        />

        <div
          style={{
            padding:
              "20px",
          }}
        >
          {usuarioSeleccionado && (
            <ModuloEditaUsuarios
              datos={
                usuarioSeleccionado
              }

              myID={() => {
                setModalEditar(
                  false
                );

                getUsuarios();
              }}
            />
          )}
        </div>
      </Modal>

      {/* ======================================================
          MODAL CREAR
      ====================================================== */}

      <Modal
        ariaHideApp={false}

        isOpen={
          modalCrear
        }

        onRequestClose={() =>
          setModalCrear(
            false
          )
        }

        style={
          modalStyles
        }
      >
        <ModalHeader
          titulo="Crear usuario"

          subtitulo="Nuevo usuario del sistema"

          cerrar={() =>
            setModalCrear(
              false
            )
          }
        />

        <div
          style={{
            padding:
              "20px",
          }}
        >
          <ModuloCreaUsuario
            myID={() => {
              setModalCrear(
                false
              );

              getUsuarios();
            }}
          />
        </div>
      </Modal>

      {/* ======================================================
          MODAL AULAS
      ====================================================== */}

      <Modal
        ariaHideApp={false}

        isOpen={
          modalAulas
        }

        onRequestClose={() =>
          setModalAulas(
            false
          )
        }

        style={
          modalStyles
        }
      >
        <ModalHeader
          titulo="Permisos de Aulas"

          subtitulo={
            usuarioSeleccionado
              ? `${usuarioSeleccionado.apellido || ""}, ${
                  usuarioSeleccionado.nombre ||
                  ""
                }`
              : ""
          }

          cerrar={() =>
            setModalAulas(
              false
            )
          }
        />

        {usuarioSeleccionado && (
          <GestionAulasUsuario
            usuario={obtenerIdentificacion(
              usuarioSeleccionado
            )}
          />
        )}
      </Modal>

      <div
        style={{
          marginTop:
            "30px",
        }}
      >
        <FooterCorp />
      </div>
    </div>
  );
}

// ============================================================
// FILA USUARIO
// ============================================================

function UsuarioFila({
  usuario,
  destino,
  seleccionado,
  onClick,
}) {
  const nombre =
    `${usuario.apellido || ""} ${
      usuario.nombre || ""
    }`.trim();

  const identificacion =
    usuario.identificacion ||
    "";

  const activo =
    String(
      usuario.estado
    ) === "1";

  return (
    <div
      onClick={
        onClick
      }

      style={{
        padding:
          "15px 20px",

        borderBottom:
          "1px solid #edf2f7",

        borderLeft:
          seleccionado
            ? "4px solid #2563eb"
            : "4px solid transparent",

        background:
          seleccionado
            ? "#eff6ff"
            : "#fff",

        cursor:
          "pointer",

        display:
          "flex",

        justifyContent:
          "space-between",

        alignItems:
          "center",

        gap:
          "15px",
      }}
    >
      <div
        style={{
          minWidth:
            0,
        }}
      >
        <div
          style={{
            fontSize:
              "16px",

            fontWeight:
              700,

            color:
              "#172033",
          }}
        >
          {nombre}
        </div>

        <div
          style={{
            color:
              "#64748b",

            fontSize:
              "13px",

            marginTop:
              "3px",
          }}
        >
          {identificacion &&
            `DNI ${identificacion}`}
        </div>

        <div
          style={{
            color:
              "#047857",

            fontWeight:
              600,

            fontSize:
              "12px",

            marginTop:
              "4px",
          }}
        >
          🏢 {destino}
        </div>
      </div>

      <div
        style={{
          display:
            "flex",

          alignItems:
            "center",

          gap:
            "8px",

          flexWrap:
            "wrap",

          justifyContent:
            "flex-end",
        }}
      >
        <span className="badge bg-secondary">
          Nivel{" "}
          {usuario.nivel}
        </span>

        <span
          className={
            activo
              ? "badge bg-success"
              : "badge bg-danger"
          }
        >
          {activo
            ? "Activo"
            : "Inactivo"}
        </span>

        <span
          style={{
            color:
              "#94a3b8",

            fontSize:
              "20px",
          }}
        >
          ›
        </span>
      </div>
    </div>
  );
}

// ============================================================
// GESTIÓN DE AULAS
// ============================================================

function GestionAulasUsuario({
  usuario,
}) {
  const [
    clases,
    setClases,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState(null);

  const [
    eliminandoId,
    setEliminandoId,
  ] = useState(null);

  const [
    agregando,
    setAgregando,
  ] = useState(false);

  const [
    dependenciasSeleccionadas,
    setDependenciasSeleccionadas,
  ] = useState([]);

  const [
    categoriasExtra,
    setCategoriasExtra,
  ] = useState("");

  const [
    cursosExtra,
    setCursosExtra,
  ] = useState("");

  const cancelTokenRef =
    useRef(null);

  // ==========================================================
  // OBTENER CLASES
  // ==========================================================

  const obtenerClases =
    async () => {
      if (!usuario) {
        return;
      }

      try {
        setLoading(
          true
        );

        setError(
          null
        );

        if (
          cancelTokenRef.current
        ) {
          cancelTokenRef.current.cancel();
        }

        cancelTokenRef.current =
          axios.CancelToken.source();

        const response =
          await axios.post(
            `https://wwwa.isepsantafe.net.ar/expedientes/moodles/obtenerClasesPorNI/${usuario}`,

            {},

            {
              headers: {
                "Content-Type":
                  "application/json",
              },

              cancelToken:
                cancelTokenRef
                  .current
                  .token,
            }
          );

        setClases(
          response.data
            ?.data ||
            []
        );
      } catch (err) {
        if (
          !axios.isCancel(
            err
          )
        ) {
          console.error(
            err
          );

          setError(
            "Error al obtener las clases"
          );
        }
      } finally {
        setLoading(
          false
        );
      }
    };

  useEffect(() => {
    if (!usuario) {
      return;
    }

    obtenerClases();

    return () => {
      if (
        cancelTokenRef.current
      ) {
        cancelTokenRef.current.cancel();
      }
    };
  }, [usuario]);

  // ==========================================================
  // DEPENDENCIAS MOODLE
  // ==========================================================

  const toggleDependencia =
    (value) => {
      if (
        value === 0
      ) {
        if (
          dependenciasSeleccionadas.includes(
            0
          )
        ) {
          setDependenciasSeleccionadas(
            []
          );
        } else {
          setDependenciasSeleccionadas(
            OPCIONES_DEPENDENCIAS.map(
              (opcion) =>
                opcion.value
            )
          );
        }

        return;
      }

      const nuevas =
        dependenciasSeleccionadas.includes(
          value
        )
          ? dependenciasSeleccionadas.filter(
              (v) =>
                v !==
                value
            )
          : [
              ...dependenciasSeleccionadas.filter(
                (v) =>
                  v !==
                  0
              ),

              value,
            ];

      setDependenciasSeleccionadas(
        nuevas
      );
    };

  // ==========================================================
  // ELIMINAR
  // ==========================================================

  const eliminarRegistro =
    async (
      registro
    ) => {
      const confirmar =
        await Swal.fire(
          {
            title:
              "¿Desmatricular?",

            text:
              "Esta acción eliminará la matrícula",

            icon:
              "warning",

            showCancelButton:
              true,

            confirmButtonText:
              "Sí, eliminar",

            cancelButtonText:
              "Cancelar",
          }
        );

      if (
        !confirmar.isConfirmed
      ) {
        return;
      }

      try {
        setEliminandoId(
          registro.id
        );

        await axios.post(
          "https://wwwa.isepsantafe.net.ar/expedientes/moodles/desmatricularPorRegistro",

          {
            identificacionUsuario:
              registro.identificacionUsuario,

            id:
              registro.id,

            platformUrl:
              "https://cursos.isepsantafe.net.ar",
          }
        );

        await obtenerClases();

        Swal.fire(
          "Eliminado",

          "Registro eliminado correctamente",

          "success"
        );
      } catch (err) {
        console.error(
          err
        );

        Swal.fire(
          "Error",

          "No se pudo eliminar",

          "error"
        );
      } finally {
        setEliminandoId(
          null
        );
      }
    };

  // ==========================================================
  // AGREGAR
  // ==========================================================

  const agregarRegistro =
    async () => {
      if (
        !dependenciasSeleccionadas.length &&
        !categoriasExtra &&
        !cursosExtra
      ) {
        Swal.fire(
          "Atención",

          "Debe seleccionar dependencia o ingresar IDs",

          "warning"
        );

        return;
      }

      try {
        setAgregando(
          true
        );

        let todasLasMaterias =
          [];

        // ====================================================
        // CATEGORÍAS PRINCIPALES
        // ====================================================

        for (
          const dependencia
          of dependenciasSeleccionadas
        ) {
          if (
            dependencia ===
            0
          ) {
            continue;
          }

          const responseCategorias =
            await axios.post(
              `https://wwwp.isepsantafe.net.ar/moodleservice/categorias_id?id_categoria=${dependencia}&plataforma=cursos`
            );

          const cursos =
            responseCategorias
              .data
              ?.cursos ||
            [];

          const idsCursos =
            cursos
              .filter(
                (curso) =>
                  curso.visible
              )
              .map(
                (curso) =>
                  curso.id
              );

          todasLasMaterias =
            [
              ...todasLasMaterias,

              ...idsCursos,
            ];
        }

        // ====================================================
        // CATEGORÍAS EXTRA
        // ====================================================

        const categoriasExtraArray =
          categoriasExtra
            .trim()
            .split(
              /\s+/
            )
            .map(
              (id) =>
                parseInt(
                  id
                )
            )
            .filter(
              (id) =>
                !isNaN(
                  id
                )
            );

        for (
          const categoria
          of categoriasExtraArray
        ) {
          const responseCategorias =
            await axios.post(
              `https://wwwp.isepsantafe.net.ar/moodleservice/categorias_id?id_categoria=${categoria}&plataforma=cursos`
            );

          const cursos =
            responseCategorias
              .data
              ?.cursos ||
            [];

          const idsCursos =
            cursos
              .filter(
                (curso) =>
                  curso.visible
              )
              .map(
                (curso) =>
                  curso.id
              );

          todasLasMaterias =
            [
              ...todasLasMaterias,

              ...idsCursos,
            ];
        }

        // ====================================================
        // CURSOS DIRECTOS
        // ====================================================

        const cursosExtraArray =
          cursosExtra
            .trim()
            .split(
              /\s+/
            )
            .map(
              (id) =>
                parseInt(
                  id
                )
            )
            .filter(
              (id) =>
                !isNaN(
                  id
                )
            );

        todasLasMaterias =
          [
            ...todasLasMaterias,

            ...cursosExtraArray,
          ];

        // ====================================================
        // SIN DUPLICADOS
        // ====================================================

        todasLasMaterias =
          [
            ...new Set(
              todasLasMaterias
            ),
          ];

        if (
          !todasLasMaterias.length
        ) {
          Swal.fire(
            "Sin cursos",

            "No se encontraron cursos",

            "info"
          );

          return;
        }

        // ====================================================
        // CONFIRMAR
        // ====================================================

        const confirmacion =
          await Swal.fire(
            {
              title:
                "Confirmar Matriculación",

              html: `
                <div style="text-align:left">
                  <b>Usuario:</b> ${usuario}<br/>
                  <b>Total cursos:</b> ${todasLasMaterias.length}<br/>
                  <b>Clase:</b> 9<br/><br/>
                  ¿Desea continuar?
                </div>
              `,

              icon:
                "question",

              showCancelButton:
                true,

              confirmButtonText:
                "Sí, matricular",

              cancelButtonText:
                "Cancelar",
            }
          );

        if (
          !confirmacion.isConfirmed
        ) {
          return;
        }

        // ====================================================
        // GUARDAR
        // ====================================================

        await axios.post(
          "https://wwwa.isepsantafe.net.ar/expedientes/moodles/guardarClaseUsuario",

          {
            identificacionUsuario:
              usuario,

            clase:
              9,

            materiasIdMoodle:
              todasLasMaterias,

            platformUrl:
              "https://cursos.isepsantafe.net.ar",
          }
        );

        Swal.fire(
          "Éxito",

          "Usuario matriculado correctamente",

          "success"
        );

        setDependenciasSeleccionadas(
          []
        );

        setCategoriasExtra(
          ""
        );

        setCursosExtra(
          ""
        );

        await obtenerClases();
      } catch (err) {
        console.error(
          err
        );

        Swal.fire(
          "Error",

          "Ocurrió un error al matricular",

          "error"
        );
      } finally {
        setAgregando(
          false
        );
      }
    };

  // ==========================================================
  // RENDER AULAS
  // ==========================================================

  return (
    <div
      style={{
        padding:
          "22px",
      }}
    >
      {/* ======================================================
          CABECERA
      ====================================================== */}

      <div
        style={{
          background:
            "#eff6ff",

          border:
            "1px solid #bfdbfe",

          borderRadius:
            "12px",

          padding:
            "14px 16px",

          marginBottom:
            "18px",
        }}
      >
        <strong>
          🎓 Clases del usuario
        </strong>

        <div
          style={{
            marginTop:
              "4px",

            color:
              "#475569",
          }}
        >
          Identificación:{" "}

          <strong>
            {usuario}
          </strong>
        </div>
      </div>

      {/* ======================================================
          NUEVA MATRÍCULA
      ====================================================== */}

      <div
        style={{
          border:
            "1px solid #e2e8f0",

          borderRadius:
            "14px",

          overflow:
            "hidden",

          marginBottom:
            "20px",
        }}
      >
        <div
          style={{
            padding:
              "13px 16px",

            background:
              "#f8fafc",

            borderBottom:
              "1px solid #e2e8f0",

            fontWeight:
              700,
          }}
        >
          ➕ Agregar permisos de aulas
        </div>

        <div
          style={{
            padding:
              "18px",
          }}
        >
          <div
            style={{
              marginBottom:
                "18px",
            }}
          >
            <strong>
              Seleccionar dependencias
            </strong>

            <div
              style={{
                display:
                  "flex",

                flexWrap:
                  "wrap",

                gap:
                  "9px",

                marginTop:
                  "10px",
              }}
            >
              {OPCIONES_DEPENDENCIAS.map(
                (opcion) => {
                  const seleccionada =
                    dependenciasSeleccionadas.includes(
                      opcion.value
                    );

                  return (
                    <label
                      key={
                        opcion.value
                      }

                      style={{
                        cursor:
                          "pointer",

                        display:
                          "flex",

                        alignItems:
                          "center",

                        gap:
                          "7px",

                        padding:
                          "9px 12px",

                        border:
                          seleccionada
                            ? "1px solid #2563eb"
                            : "1px solid #cbd5e1",

                        borderRadius:
                          "9px",

                        background:
                          seleccionada
                            ? "#eff6ff"
                            : "#fff",
                      }}
                    >
                      <input
                        type="checkbox"

                        checked={
                          seleccionada
                        }

                        onChange={() =>
                          toggleDependencia(
                            opcion.value
                          )
                        }
                      />

                      {
                        opcion.label
                      }
                    </label>
                  );
                }
              )}
            </div>
          </div>

          <div
            style={{
              display:
                "grid",

              gridTemplateColumns:
                "repeat(auto-fit,minmax(280px,1fr))",

              gap:
                "15px",
            }}
          >
            <div>
              <label
                style={{
                  fontWeight:
                    600,

                  marginBottom:
                    "5px",
                }}
              >
                ID Categorías extra
              </label>

              <input
                type="text"

                className="form-control"

                placeholder="Ej: 300 455 890"

                value={
                  categoriasExtra
                }

                onChange={(e) =>
                  setCategoriasExtra(
                    e.target.value
                  )
                }
              />

              <small className="text-muted">
                Separadas por espacio.
              </small>
            </div>

            <div>
              <label
                style={{
                  fontWeight:
                    600,

                  marginBottom:
                    "5px",
                }}
              >
                ID Cursos directos
              </label>

              <input
                type="text"

                className="form-control"

                placeholder="Ej: 1200 1400 1500"

                value={
                  cursosExtra
                }

                onChange={(e) =>
                  setCursosExtra(
                    e.target.value
                  )
                }
              />

              <small className="text-muted">
                Separados por espacio.
              </small>
            </div>
          </div>

          <div
            style={{
              marginTop:
                "18px",

              display:
                "flex",

              justifyContent:
                "flex-end",
            }}
          >
            <button
              type="button"

              className="btn btn-success"

              onClick={
                agregarRegistro
              }

              disabled={
                agregando
              }
            >
              {agregando
                ? "Procesando..."
                : "＋ Agregar Clase"}
            </button>
          </div>
        </div>
      </div>

      {/* ======================================================
          CLASES ACTUALES
      ====================================================== */}

      <div
        style={{
          border:
            "1px solid #e2e8f0",

          borderRadius:
            "14px",

          overflow:
            "hidden",
        }}
      >
        <div
          style={{
            padding:
              "13px 16px",

            background:
              "#f8fafc",

            display:
              "flex",

            justifyContent:
              "space-between",

            alignItems:
              "center",

            borderBottom:
              "1px solid #e2e8f0",
          }}
        >
          <strong>
            Aulas asignadas
          </strong>

          <button
            type="button"

            className="btn btn-outline-primary btn-sm"

            onClick={
              obtenerClases
            }

            disabled={
              loading
            }
          >
            ↻ Actualizar
          </button>
        </div>

        <div
          style={{
            padding:
              "18px",
          }}
        >
          {loading ? (
            <div
              style={{
                padding:
                  "35px",

                textAlign:
                  "center",
              }}
            >
              <div
                className="spinner-border text-primary"
                role="status"
              />

              <div
                style={{
                  marginTop:
                    "10px",
                }}
              >
                Cargando clases...
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              {error}
            </div>
          ) : clases.length ===
            0 ? (
            <div
              style={{
                padding:
                  "30px",

                textAlign:
                  "center",

                color:
                  "#64748b",
              }}
            >
              No hay clases disponibles.
            </div>
          ) : (
            <div
              style={{
                display:
                  "grid",

                gridTemplateColumns:
                  "repeat(auto-fit,minmax(330px,1fr))",

                gap:
                  "12px",
              }}
            >
              {clases.map(
                (clase) => {
                  let materias =
                    [];

                  try {
                    materias =
                      JSON.parse(
                        clase.materiasIdMoodle ||
                          "[]"
                      );
                  } catch {
                    materias =
                      [];
                  }

                  return (
                    <div
                      key={
                        clase.id
                      }

                      style={{
                        padding:
                          "15px",

                        border:
                          "1px solid #e2e8f0",

                        borderRadius:
                          "12px",

                        background:
                          "#fff",
                      }}
                    >
                      <div
                        style={{
                          display:
                            "flex",

                          justifyContent:
                            "space-between",

                          gap:
                            "10px",

                          alignItems:
                            "center",
                        }}
                      >
                        <div>
                          <strong>
                            Registro #
                            {
                              clase.id
                            }
                          </strong>

                          <div
                            style={{
                              fontSize:
                                "13px",

                              color:
                                "#64748b",
                            }}
                          >
                            Clase{" "}
                            {
                              clase.clase
                            }
                          </div>
                        </div>

                        <span className="badge bg-primary">
                          {
                            materias.length
                          }{" "}
                          cursos
                        </span>
                      </div>

                      <div
                        style={{
                          marginTop:
                            "10px",

                          background:
                            "#f8fafc",

                          padding:
                            "10px",

                          borderRadius:
                            "8px",

                          fontSize:
                            "12px",

                          maxHeight:
                            "100px",

                          overflowY:
                            "auto",

                          wordBreak:
                            "break-word",
                        }}
                      >
                        <strong>
                          Materias Moodle:
                        </strong>

                        <br />

                        {materias.length
                          ? materias.join(
                              ", "
                            )
                          : "Sin materias"}
                      </div>

                      <button
                        type="button"

                        className="btn btn-outline-danger btn-sm mt-3"

                        onClick={() =>
                          eliminarRegistro(
                            clase
                          )
                        }

                        disabled={
                          eliminandoId ===
                          clase.id
                        }
                      >
                        {eliminandoId ===
                        clase.id
                          ? "Eliminando..."
                          : "🗑 Desmatricular"}
                      </button>
                    </div>
                  );
                }
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TARJETA RESUMEN
// ============================================================

function TarjetaResumen({
  icono,
  titulo,
  valor,
}) {
  return (
    <div
      style={{
        background:
          "#fff",

        padding:
          "18px",

        borderRadius:
          "15px",

        boxShadow:
          "0 3px 12px rgba(15,23,42,.07)",

        display:
          "flex",

        alignItems:
          "center",

        gap:
          "14px",
      }}
    >
      <div
        style={{
          fontSize:
            "29px",
        }}
      >
        {icono}
      </div>

      <div>
        <div
          style={{
            color:
              "#64748b",

            fontSize:
              "13px",
          }}
        >
          {titulo}
        </div>

        <div
          style={{
            color:
              "#1e293b",

            fontWeight:
              700,

            fontSize:
              "24px",
          }}
        >
          {valor}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DATO USUARIO
// ============================================================

function DatoUsuario({
  titulo,
  valor,
}) {
  if (
    valor === undefined ||
    valor === null ||
    valor === ""
  ) {
    return null;
  }

  return (
    <div
      style={{
        marginBottom:
          "13px",
      }}
    >
      <div
        style={{
          fontSize:
            "12px",

          color:
            "#64748b",
        }}
      >
        {titulo}
      </div>

      <div
        style={{
          fontWeight:
            600,

          color:
            "#1e293b",
        }}
      >
        {String(valor)}
      </div>
    </div>
  );
}

// ============================================================
// HEADER MODAL
// ============================================================

function ModalHeader({
  titulo,
  subtitulo,
  cerrar,
}) {
  return (
    <div
      style={{
        position:
          "sticky",

        top: 0,

        zIndex:
          50,

        padding:
          "17px 22px",

        background:
          "linear-gradient(135deg,#1e3a5f,#32739a)",

        color:
          "#fff",

        display:
          "flex",

        justifyContent:
          "space-between",

        alignItems:
          "center",

        gap:
          "15px",
      }}
    >
      <div>
        <h5
          style={{
            margin:
              0,
          }}
        >
          {titulo}
        </h5>

        {subtitulo && (
          <div
            style={{
              marginTop:
                "3px",

              fontSize:
                "13px",

              opacity:
                0.8,
            }}
          >
            {subtitulo}
          </div>
        )}
      </div>

      <button
        type="button"

        onClick={
          cerrar
        }

        style={{
          width:
            "38px",

          height:
            "38px",

          border:
            "none",

          borderRadius:
            "9px",

          color:
            "#fff",

          background:
            "rgba(255,255,255,.15)",

          fontSize:
            "21px",
        }}
      >
        ×
      </button>
    </div>
  );
}

export default ModuloUsuarios;